import * as RadixPopover from '@radix-ui/react-popover';

import { cn } from '../../utils/cn';
import { DropdownMenu } from '../DropdownMenu';
import { DROPDOWN_MENU_PADDING_PX } from '../DropdownMenu/DropdownMenu.variants';
import { MenuHighlight } from '../DropdownMenu/MenuHighlight';
import type { DropdownSubContentProps } from './Dropdown.types';
import { useDropdownContext, useDropdownSubContext } from './DropdownContext';

/**
 * Submenu content — **web** implementation.
 *
 * Portals a side-flyout `DropdownMenu` panel next to the trigger row
 * (prefers `right`, Radix flips to `left` on collision). Hovering the flyout
 * cancels the trigger's scheduled close; leaving it schedules one — the same
 * grace-delay dance shadcn's submenus do.
 *
 * Marked with `data-flashka-dropdown-sub` so the parent `DropdownContent`'s
 * outside-dismiss guard never treats flyout interactions as "outside" (the
 * flyout is portaled to `body`, i.e. outside the parent's DOM subtree).
 */
export function DropdownSubContent({
  children,
  minWidth = 180,
  sideOffset = 4,
  alignOffset = -8,
  className,
}: DropdownSubContentProps) {
  const { size, testID } = useDropdownContext('DropdownSubContent');
  const { cancelClose, scheduleClose } = useDropdownSubContext('DropdownSubContent');

  return (
    <RadixPopover.Portal>
      <RadixPopover.Content
        side="right"
        align="start"
        // The anchor is the trigger ROW, which sits inside the parent panel's
        // 8-px padding — so the offset Radix measures from is 8 px short of the
        // panel's border. Add it back to keep `sideOffset` meaning what it says:
        // the gap between the parent PANEL and the flyout.
        sideOffset={sideOffset + DROPDOWN_MENU_PADDING_PX}
        alignOffset={alignOffset}
        collisionPadding={12}
        // Hover-open must not steal focus from the parent menu (and a focus
        // jump would end the hover on the trigger, closing the flyout).
        onOpenAutoFocus={(e) => e.preventDefault()}
        data-flashka-dropdown-sub=""
        onMouseEnter={cancelClose}
        onMouseLeave={scheduleClose}
        // Same open/close morph as the parent panel — without it the flyout
        // blinks out instantly while the menu it belongs to animates away.
        className="flashka-popup-content"
        style={{
          minWidth,
          zIndex: 1001,
          transformOrigin: 'var(--radix-popover-content-transform-origin)',
        }}>
        <DropdownMenu
          size={size}
          className={cn('flex flex-col', className)}
          testID={testID ? `${testID}-subpopup` : undefined}>
          {/* The flyout is its own panel, so it needs its own sliding
              indicator — otherwise its rows would be the only ones in the menu
              still cross-fading their hover. */}
          <MenuHighlight className="flex flex-col gap-1">{children}</MenuHighlight>
        </DropdownMenu>
      </RadixPopover.Content>
    </RadixPopover.Portal>
  );
}
